import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
  DriveFileDto,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import {
  getClassroomScopes,
  getListFilesQueryKey,
  googleLogin,
  useDeleteFile,
  useGetDriveFiles,
  useListFiles,
} from "@packages/api/madlenAPI";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Text, Pagination } from "../../../../../core";
import CourseSelector from "../course-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { FileUploader } from "@core/file-uploader";
import { useAppSelector } from "@packages/store/hooks";
import GenericFileCard, { GenericFileItem } from "../GenericFileCard.tsx";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector.tsx";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

const loginClassroom = async () => {
  try {
    const neededClassroomScopesResponse = await getClassroomScopes();
    const googleLoginRes = await googleLogin({
      scopes: neededClassroomScopesResponse,
      redirectUrl: window.location.href,
    });
    window.location.href = googleLoginRes.redirectUrl;
    return true;
  } catch (e) {
    console.error(e);
  }
};

const FileStep = ({
  assignmentData,
  onChange,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}) => {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState(assignmentData.prompt || "");
  const [selectedGrade, setSelectedGrade] = useState(assignmentData.grade || 8);
  const getInitialCurriculum = () =>
    i18n.language === "tr" ? "MEB" : "INTERNATIONAL";
  const getInitialInternationalCurriculum = () => "AP";
  const getInitialCourse = (curriculum: CurriculumType) => {
    if (curriculum === "MEB") {
      return "HISTORY";
    } else {
      return "AP_2025_WORLD_HISTORY_MODERN";
    }
  };
  const [curriculumType, setCurriculumType] = useState<CurriculumType>(
    getInitialCurriculum(),
  );
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>(getInitialInternationalCurriculum());
  const [selectedCourse, setSelectedCourse] = useState<GetTopicsCourseType>(
    assignmentData.courseType || getInitialCourse(curriculumType),
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const [didGetDriveFileAccess, setDidGetDriveFileAccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<GenericFileItem[]>([]);
  const [uploadMode, setUploadMode] = useState<"drive" | "upload" | null>(
    "upload",
  );
  const [selectedUploadedFiles, setSelectedUploadedFiles] = useState<number[]>(
    [],
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(8);
  const [uploadedFilesCurrentPage, setUploadedFilesCurrentPage] = useState(1);
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" ||
    userEmail === "veyis@madlen.io" ||
    userEmail === "kadriyezobu@gmail.com";
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState<number>();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [driveSearchTerm, setDriveSearchTerm] = useState("");
  const listFilesQueryKey = getListFilesQueryKey();
  const [thumbnailErrorMap, setThumbnailErrorMap] = useState<
    Record<string | number, boolean>
  >({});
  const [uploadedFileSearchTerm, setUploadedFileSearchTerm] = useState("");

  const {
    data: uploadedFilesData,
    isLoading: isUploadedFilesLoading,
    isFetching: isUploadedFilesFetching,
  } = useListFiles(
    {
      page: uploadedFilesCurrentPage,
      size: pageSize,
      ...(uploadedFileSearchTerm.trim() && {
        fileName: uploadedFileSearchTerm.trim(),
      }),
    },
    {
      query: {
        refetchOnWindowFocus: false,
      },
    },
  );

  useEffect(() => {
    const newCurriculumType = getInitialCurriculum();
    setCurriculumType(newCurriculumType);
    setInternationalCurriculum(getInitialInternationalCurriculum());
    const newCourse = getInitialCourse(newCurriculumType);
    setSelectedCourse(newCourse);
    setTopic("");

    onChange({
      courseType: newCourse,
      prompt: "",
    });
  }, [i18n.language]);

  const deleteFile = useDeleteFile({
    mutation: {
      onMutate: (data) => {
        setIsDeleting(data.id);
      },
      onSettled: () => {
        setIsDeleting(undefined);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListFilesQueryKey({
            page: uploadedFilesCurrentPage,
            size: pageSize,
            ...(uploadedFileSearchTerm.trim() && {
              fileName: uploadedFileSearchTerm.trim(),
            }),
          }),
        });
      },
    },
  });

  const {
    data: driveFilesData,
    error: driveFilesError,
    refetch: refetchDriveFiles,
    isLoading: isDriveFilesLoading,
    isFetching: isDriveFilesFetching,
  } = useGetDriveFiles(
    {
      page: currentPage,
      size: pageSize,
      ...(driveSearchTerm.trim() && { searchQuery: driveSearchTerm.trim() }),
    },
    {
      query: {
        enabled: didGetDriveFileAccess,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  );

  useEffect(() => {
    if (
      driveFilesError?.response?.status === 401 ||
      driveFilesError?.response?.status === 404
    ) {
      loginClassroom();
    }
  }, [driveFilesError, refetchDriveFiles]);

  const handleDriveSelect = async () => {
    setUploadMode("drive");
    setDidGetDriveFileAccess(true);
    setCurrentPage(1);
    setSelectedFiles([]);
    onChange({
      generator: "DRIVE_FILE",
      driveFileIds: [],
      teacherDocumentIds: [],
    });
  };

  const handleUploadSelect = () => {
    setUploadMode("upload");
    setSelectedFiles([]);
    onChange({
      generator: "TEACHER_DOCUMENT",
      driveFileIds: [],
      teacherDocumentIds: [],
    });
  };

  useEffect(() => {
    if (uploadMode === "drive") {
      onChange({ generator: "DRIVE_FILE" });
    } else if (uploadMode === "upload") {
      onChange({ generator: "TEACHER_DOCUMENT" });
    }
  }, [uploadMode]);

  const toggleFileSelection = (file: GenericFileItem) => {
    setSelectedFiles([file]);
    if (uploadMode === "drive") {
      onChange({ driveFileIds: [file.id as string], teacherDocumentIds: [] });
    } else if (uploadMode === "upload") {
      onChange({ teacherDocumentIds: [file.id as number], driveFileIds: [] });
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (driveFilesData?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const isLoadingDrive = isDriveFilesLoading || isDriveFilesFetching;
  const isLoadingUploaded = isUploadedFilesLoading || isUploadedFilesFetching;

  const driveFiles = driveFilesData?.data;

  const handleUploadedFileSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setUploadedFileSearchTerm(e.target.value);
    setUploadedFilesCurrentPage(1);
  };

  const handleClearUploadedFileSearch = () => {
    setUploadedFileSearchTerm("");
    setUploadedFilesCurrentPage(1);
  };

  const handleUploadedFilesPreviousPage = () => {
    if (uploadedFilesCurrentPage > 1) {
      setUploadedFilesCurrentPage(uploadedFilesCurrentPage - 1);
    }
  };

  const handleUploadedFilesNextPage = () => {
    if (
      uploadedFilesData?.data &&
      uploadedFilesData.page &&
      uploadedFilesData.totalPages &&
      uploadedFilesCurrentPage < uploadedFilesData.totalPages
    ) {
      setUploadedFilesCurrentPage(uploadedFilesCurrentPage + 1);
    }
  };

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg mb-6">
        <Text variant="p" status="info" size="sm">
          assignments.generator.file.title
        </Text>
        <Text variant="p" status="info" size="sm">
          assignments.generator.file.description
        </Text>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade: number) => {
                const availableCourses = Object.entries(CourseData).filter(
                  ([, value]) => value.visible(grade),
                );
                const firstAvailableCourse =
                  availableCourses[0]?.[0] as GetTopicsCourseType;
                const doesPreviousCourseExist = availableCourses.some(
                  ([key]) => key === selectedCourse,
                );
                setSelectedGrade(grade);
                if (!doesPreviousCourseExist) {
                  setSelectedCourse(firstAvailableCourse);
                }
                onChange({
                  grade,
                  ...(!doesPreviousCourseExist && {
                    courseType: firstAvailableCourse,
                  }),
                  topicIds: [],
                });
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language });
            }}
          />
        </div>
      </div>
      <div className="space-y-4">
        {/* Curriculum Type Selection */}
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-2 gap-3">
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("MEB");
                const mebCourses = Object.keys(CourseData).filter(
                  (key) =>
                    isTurkishCourse(key) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];

                if (mebCourses.length > 0) {
                  setSelectedCourse(mebCourses[0]);
                  onChange({
                    courseType: mebCourses[0],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("INTERNATIONAL");
                const intlCourses = Object.keys(CourseData).filter(
                  (key) =>
                    key.startsWith(`${internationalCurriculum}_`) &&
                    CourseData[key as GetTopicsCourseType].visible(
                      selectedGrade,
                    ),
                ) as GetTopicsCourseType[];
                if (intlCourses.length > 0) {
                  setSelectedCourse(intlCourses[0]);
                  onChange({
                    courseType: intlCourses[0],
                  });
                }
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "INTERNATIONAL"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "INTERNATIONAL"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.internationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>

        {/* International Curriculum Sub-selection */}
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => {
                    setInternationalCurriculum(curriculum);
                    const intlCourses = Object.keys(CourseData).filter(
                      (key) =>
                        key.startsWith(`${curriculum}_`) &&
                        CourseData[key as GetTopicsCourseType].visible(
                          selectedGrade,
                        ),
                    ) as GetTopicsCourseType[];

                    if (intlCourses.length > 0) {
                      setSelectedCourse(intlCourses[0]);
                      onChange({
                        courseType: intlCourses[0],
                      });
                    }
                  }}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              selectedCourse={selectedCourse}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                onChange({
                  grade: selectedGrade,
                  courseType: course,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="animate-fadeIn">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {t("assignments.generator.properties.topic")}
        </label>
        <div className="relative">
          <textarea
            data-testid="create-assignment-from-drive-file-topic-input"
            value={topic}
            onChange={(e) => {
              setTopic(e.target.value);
              onChange({ prompt: e.target.value });
            }}
            className="w-full h-24 p-4 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
            placeholder={t("assignments.generator.file.topicPlaceholder")}
          />
          <div className="absolute right-3 bottom-3 text-xs text-gray-400">
            {topic.length}/200
          </div>
        </div>
        <Text
          variant="p"
          className="mt-2 text-sm text-gray-500 dark:text-gray-400"
        >
          assignments.generator.properties.topicDesc
        </Text>
      </div>

      <div className="flex space-x-4 mb-6">
        <Button
          data-testid="create-assignment-from-drive-file-drive-button"
          rounded="lg"
          bgColor={uploadMode === "drive" ? "bg-blue-50 dark:bg-blue-900" : ""}
          size="md"
          fullWidth
          className={`border-2 ${
            uploadMode === "drive"
              ? "border-blue-500"
              : "border-gray-200 hover:border-blue-300"
          }`}
          onClick={handleDriveSelect}
        >
          <div className="flex items-center justify-center">
            <svg className="w-6 h-6 mr-2" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <Text variant="span" status="secondary" size="base">
              assignments.generator.file.choose
            </Text>
          </div>
        </Button>

        <Button
          data-testid="create-assignment-from-file-button2"
          rounded="lg"
          fullWidth
          size="icon"
          bgColor={uploadMode === "upload" ? "bg-blue-50 dark:bg-blue-900" : ""}
          className={`border-2 ${
            uploadMode === "upload"
              ? "border-blue-500"
              : "border-gray-200 hover:border-blue-300"
          }`}
          onClick={handleUploadSelect}
        >
          <div className="flex items-center justify-center">
            <svg
              className="w-6 h-6 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            <Text variant="span" status="secondary" size="base">
              assignments.generator.file.upload
            </Text>
          </div>
        </Button>
      </div>

      {uploadMode === "upload" && (
        <>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
            {t("assignments.generator.file.upload")}
          </label>

          <FileUploader
            onUploadSuccess={(url, response) => {
              queryClient.invalidateQueries({ queryKey: listFilesQueryKey });
              if (response) {
                const newFileItem: GenericFileItem = {
                  id: response.id!,
                  fileName: response.fileName,
                  viewOrDownloadLink: response.url || response.downloadUrl,
                };
                toggleFileSelection(newFileItem);
              }
            }}
            onFileDelete={() => {
              setSelectedFiles([]);
              onChange({ teacherDocumentIds: [] });
            }}
            onUploadError={(error) => {
              setUploadError(error);
            }}
          />
          <Text variant="p" status="error" size="sm">
            {uploadError}
          </Text>

          <div className="mt-6 mb-4">
            <div className="relative">
              <input
                type="text"
                value={uploadedFileSearchTerm}
                onChange={(e) => setUploadedFileSearchTerm(e.target.value)}
                placeholder={t("assignments.generator.file.searchPlaceholder")}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {uploadedFileSearchTerm && (
                <button
                  onClick={() => setUploadedFileSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {isLoadingUploaded && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {Array.from({ length: pageSize > 3 ? pageSize : 4 }).map(
                  (_, index) => (
                    <GenericFileCard
                      key={`skeleton-upload-${index}`}
                      isLoading={true}
                    />
                  ),
                )}
              </div>
            )}

            {!isLoadingUploaded &&
              uploadedFilesData?.data &&
              uploadedFilesData.data.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {uploadedFilesData.data.map(
                    (file: TeacherDocumentResponseDto) => {
                      const fileItem: GenericFileItem = {
                        id: file.id!,
                        fileName: file.fileName,
                        viewOrDownloadLink: file.url || file.downloadUrl,
                      };
                      return (
                        <GenericFileCard
                          key={file.id}
                          file={fileItem}
                          isSelected={selectedFiles.some(
                            (sf) => sf.id === file.id,
                          )}
                          onSelect={() => toggleFileSelection(fileItem)}
                          isBeingDeleted={isDeleting === file.id}
                          onView={() => {
                            if (file.url || file.downloadUrl) {
                              window.open(
                                file.url || file.downloadUrl!,
                                "_blank",
                              );
                            }
                          }}
                          onDelete={() =>
                            deleteFile.mutateAsync({ id: file.id! })
                          }
                        />
                      );
                    },
                  )}
                </div>
              )}
            {!isLoadingUploaded &&
              uploadedFilesData?.data?.length === 0 &&
              uploadedFileSearchTerm && (
                <div className="text-center py-4">
                  <Text variant="p" status="secondary" size="base">
                    {t("assignments.generator.file.noSearchResults")}
                  </Text>
                </div>
              )}
            {!isLoadingUploaded &&
              (!uploadedFilesData?.data ||
                uploadedFilesData.data.length === 0) &&
              !uploadedFileSearchTerm && (
                <div className="text-center py-4">
                  <Text variant="p" status="secondary" size="base">
                    {t("assignments.generator.file.noFilesUploadedYet")}
                  </Text>
                </div>
              )}
          </div>
          {(uploadedFilesCurrentPage > 1 ||
            (uploadedFilesData?.data &&
              uploadedFilesData.page &&
              uploadedFilesData.totalPages &&
              uploadedFilesCurrentPage < uploadedFilesData.totalPages) ||
            (isLoadingUploaded && uploadedFilesCurrentPage > 0)) && (
            <div className="mt-4 flex justify-center items-center gap-4">
              <button
                onClick={handleUploadedFilesPreviousPage}
                disabled={uploadedFilesCurrentPage <= 1 || isLoadingUploaded}
                className="px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t("common.previous")}
              </button>
              <span className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                {t("common.page")} {uploadedFilesCurrentPage}
                {isLoadingUploaded
                  ? "..."
                  : uploadedFilesData?.data &&
                      uploadedFilesData.page &&
                      uploadedFilesData.totalPages &&
                      uploadedFilesCurrentPage < uploadedFilesData.totalPages
                    ? ""
                    : t("common.pagination.lastPage")}
              </span>
              <button
                onClick={handleUploadedFilesNextPage}
                disabled={
                  isLoadingUploaded ||
                  !(
                    uploadedFilesData?.data &&
                    uploadedFilesData.page &&
                    uploadedFilesData.totalPages &&
                    uploadedFilesCurrentPage < uploadedFilesData.totalPages
                  )
                }
                className="px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </>
      )}

      {uploadMode === "drive" && (
        <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <Text variant="h3">assignments.generator.file.files</Text>
          </div>
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={driveSearchTerm}
                onChange={(e) => {
                  setDriveSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder={t("assignments.generator.file.searchPlaceholder")}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              {driveSearchTerm && (
                <button
                  onClick={() => {
                    setDriveSearchTerm("");
                    setCurrentPage(1);
                  }}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg
                    className="h-5 w-5 text-gray-400 hover:text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            {isLoadingDrive &&
              Array.from({ length: pageSize }).map((_, index) => (
                <GenericFileCard
                  key={`skeleton-drive-${index}`}
                  isLoading={true}
                />
              ))}
            {!isLoadingDrive &&
              driveFiles?.map((driveFile: DriveFileDto) => {
                const fileItem: GenericFileItem = {
                  id: driveFile.id!,
                  title: driveFile.title,
                  thumbnailUrl: driveFile.thumbnailUrl,
                  alternateLink: driveFile.alternateLink,
                };
                return (
                  <GenericFileCard
                    key={driveFile.id}
                    file={fileItem}
                    isSelected={selectedFiles.some(
                      (sf) => sf.id === driveFile.id,
                    )}
                    onSelect={() => toggleFileSelection(fileItem)}
                    thumbnailError={thumbnailErrorMap[driveFile.id!] || false}
                    onThumbnailError={(fileId: string | number) =>
                      setThumbnailErrorMap((prevMap) => ({
                        ...prevMap,
                        [fileId]: true,
                      }))
                    }
                  />
                );
              })}
          </div>

          {(currentPage > 1 ||
            driveFilesData?.hasNextPage ||
            (isLoadingDrive && currentPage > 0)) && (
            <div className="mt-4 flex justify-center items-center gap-4">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1 || isLoadingDrive}
                className="px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t("common.previous")}
              </button>
              <span className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                {t("common.page")} {currentPage}
                {isLoadingDrive
                  ? "..."
                  : driveFilesData?.hasNextPage
                    ? ""
                    : t("common.pagination.lastPage")}
              </span>
              <button
                onClick={handleNextPage}
                disabled={isLoadingDrive || !driveFilesData?.hasNextPage}
                className="px-4 py-2 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {t("common.next")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileStep;
